import React, { useState, useCallback, useEffect } from "react";
import { MicrophoneImpl } from "../utils/MicrophoneImpl";
import { getUserMediaAudio } from "../utils/audioDevices";

// Permission-related types and interfaces
export interface MicrophonePermissionState {
  needsPermission: boolean;
  showPermissionOverlay: boolean;
  isInitializing: boolean;
}

export interface MicrophonePermissionActions {
  checkMicrophonePermission: () => Promise<PermissionState>;
  requestPermissionAndStart: () => Promise<boolean>;
  setNeedsPermission: (value: boolean) => void;
  setShowPermissionOverlay: (value: boolean) => void;
}

/**
 * Custom hook to manage microphone permissions
 * @returns Permission state and permission-related actions
 */
export const useMicrophonePermissions = (): [
  MicrophonePermissionState,
  MicrophonePermissionActions
] => {
  const [needsPermission, setNeedsPermission] = useState(false);
  const [showPermissionOverlay, setShowPermissionOverlay] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Check if the browser supports permission API
  const checkPermissionSupport = useCallback(async (): Promise<boolean> => {
    return "permissions" in navigator && "query" in navigator.permissions;
  }, []);

  // Check microphone permission status
  const checkMicrophonePermission = useCallback(async () => {
    try {
      if (!(await checkPermissionSupport())) {
        console.log("Permissions API not supported, assuming permission is needed");
        return "prompt"; // Default to prompt if permissions API not supported
      }

      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      console.log("Microphone permission status:", status.state);

      // Listen for changes to permission
      status.onchange = () => {
        console.log("Permission state changed to:", status.state);
        if (status.state === "granted") {
          setNeedsPermission(false);
        }
      };

      return status.state;
    } catch (error) {
      console.error("Error checking microphone permission:", error);
      return "prompt"; // Default to prompt if there's an error
    }
  }, [checkPermissionSupport]);

  // Request permission without starting the microphone
  const requestPermissionAndStart = useCallback(async () => {
    console.log("Requesting microphone permission...");

    let isPermissionGranted = false;

    try {
      // Get user media to trigger permission dialog
      const stream = await getUserMediaAudio();

      setIsInitializing(true);

      // Stop all tracks immediately (we just wanted the permission)
      stream.getTracks().forEach((track) => track.stop());

      // Re-check permission state
      const state = await checkMicrophonePermission();
      if (state === "granted") {
        setNeedsPermission(false);
        isPermissionGranted = true;

        // This step can take quite a while...
        await MicrophoneImpl.getInstance().start();
      }
    } catch (error) {
      console.error("Permission denied or error occurred:", error);
      isPermissionGranted = false;
    }

    // Hide the overlay after permission dialog is closed (whether granted or denied)
    setShowPermissionOverlay(false);
    setIsInitializing(false);

    return isPermissionGranted;
  }, [checkMicrophonePermission]);

  // Initial effect to check permission state
  useEffect(() => {
    // Check initial permission state
    const checkInitialPermission = async () => {
      const state = await checkMicrophonePermission();
      console.log("Initial mic permission state: ", state);
      if (state !== "granted") {
        setNeedsPermission(true);
      }
    };

    checkInitialPermission();
  }, [checkMicrophonePermission]);

  return [
    { needsPermission, showPermissionOverlay, isInitializing },
    {
      checkMicrophonePermission,
      requestPermissionAndStart,
      setNeedsPermission,
      setShowPermissionOverlay,
    },
  ];
};

/**
 * Permission overlay component shown during permission request
 */
export const PermissionOverlay: React.FC = () => {
  return <div className="permission-overlay" />;
};
